import { useRef } from "react";

import { getEnvironment } from "~/utils/environment";
import useInjectScript from "~/utils/hooks/useInjectScript";
import { useLocalization } from "~/utils/i18n/localization";

import routes from "~/routes/external";

interface GoogleSignInButtonProps {
  onCredential: (credential: string | undefined) => void;
}

const googleOauthClientId = getEnvironment().NEXT_PUBLIC_GOOGLE_CLIENT_ID || "";

export default function GoogleSignInButton({
  onCredential,
}: GoogleSignInButtonProps): JSX.Element {
  const divRef = useRef<HTMLDivElement>(null);
  const isGoogleSignInInitializedRef = useRef(false);

  const { i18n } = useLocalization();
  const language = i18n.resolvedLanguage || i18n.language || "en";

  const handleGoogleSignInReady = () => {
    const googleIdentity = window.google?.accounts.id;

    if (
      isGoogleSignInInitializedRef.current ||
      !googleIdentity ||
      !divRef.current
    ) {
      return;
    }

    isGoogleSignInInitializedRef.current = true;
    googleIdentity.initialize({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      client_id: googleOauthClientId,
      callback: (response) => onCredential(response.credential),
    });
    googleIdentity.renderButton(
      divRef.current,
      { theme: "outline", size: "large", locale: language } // customization attributes
    );
    googleIdentity.prompt(); // also display the One Tap dialog
  };

  useInjectScript({
    src: `${routes.googleAccounts}/gsi/client?hl=${language}`,
    strategy: "afterInteractive",
    defer: true,
    async: true,
    onReady: handleGoogleSignInReady,
    checkOnReadyInterval: 500, // Check every 500ms. Remove or set to 0 to disable rechecking.
    target: "head", // Load the script in the <head> instead of <body>
  });

  return <div ref={divRef} />;
}
